import { useCallback, useEffect, useState } from "react";
import {
  activityEffectValue,
  formatActivityTime,
  type MerchantActivityPage,
} from "./Activity";
import type { Locale } from "./i18n";

type Copy = (key: string, values?: Record<string, string | number>) => string;

export interface MerchantOverview {
  metrics: {
    transactionsToday: number;
    activeMembers: number;
    rewardsRedeemedToday: number;
  };
  program: {
    status: "draft" | "published";
    name: string | null;
    threshold: number | null;
  };
  locations: {
    active: number;
    publicReady: number;
  };
  recent: MerchantActivityPage;
}

type OverviewState =
  | { status: "loading" }
  | { status: "error"; permission: boolean }
  | { status: "ready"; overview: MerchantOverview };

function isOverview(value: unknown): value is MerchantOverview {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const overview = value as Record<string, unknown>;
  return (
    Boolean(overview.metrics) &&
    typeof overview.metrics === "object" &&
    Boolean(overview.program) &&
    typeof overview.program === "object" &&
    Boolean(overview.locations) &&
    typeof overview.locations === "object" &&
    Boolean(overview.recent) &&
    typeof overview.recent === "object"
  );
}

export function Overview({
  merchantId,
  locale,
  copy,
  onScan,
  initialOverview,
}: {
  merchantId: string;
  locale: Locale;
  copy: Copy;
  onScan?: () => void;
  initialOverview?: MerchantOverview;
}) {
  const [state, setState] = useState<OverviewState>(() =>
    initialOverview
      ? { status: "ready", overview: initialOverview }
      : { status: "loading" },
  );

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const response = await fetch("/v1/merchant/overview", {
        credentials: "include",
        headers: {
          "accept-language": locale,
          "x-merchant-id": merchantId,
        },
      });
      if (!response.ok) {
        setState({
          status: "error",
          permission: response.status === 401 || response.status === 403,
        });
        return;
      }
      const value: unknown = await response.json();
      setState(
        isOverview(value)
          ? { status: "ready", overview: value }
          : { status: "error", permission: false },
      );
    } catch {
      setState({ status: "error", permission: false });
    }
  }, [locale, merchantId]);

  useEffect(() => {
    if (!initialOverview) void load();
  }, [initialOverview, load]);

  if (state.status === "loading") {
    return (
      <section className="panel operations-status" aria-busy="true">
        <div className="loading-mark" aria-hidden="true" />
        <h2>{copy("merchant.dashboard.loading")}</h2>
      </section>
    );
  }
  if (state.status === "error") {
    return (
      <section className="panel empty-state" role="alert">
        <span className="empty-mark" aria-hidden="true" />
        <h2>
          {copy(
            state.permission
              ? "merchant.dashboard.permissionTitle"
              : "merchant.dashboard.errorTitle",
          )}
        </h2>
        <p>
          {copy(
            state.permission
              ? "merchant.dashboard.permissionBody"
              : "merchant.dashboard.errorBody",
          )}
        </p>
        {!state.permission && (
          <button
            className="primary-button"
            type="button"
            onClick={() => void load()}
          >
            {copy("common.retry")}
          </button>
        )}
      </section>
    );
  }

  const { overview } = state;
  return (
    <div className="overview-workspace">
      <section
        className="operations-hero"
        aria-label={copy("merchant.dashboard.statusLabel")}
      >
        <div>
          <p className="eyebrow">
            {copy(
              overview.program.status === "published"
                ? "merchant.dashboard.programPublished"
                : "merchant.dashboard.programDraft",
            )}
          </p>
          <h2>
            {overview.program.name ??
              copy("merchant.dashboard.programNotPublished")}
          </h2>
          <p>
            {overview.program.threshold
              ? copy("merchant.dashboard.programThreshold", {
                  threshold: overview.program.threshold,
                  ready: overview.locations.publicReady,
                  active: overview.locations.active,
                })
              : copy("merchant.dashboard.publishPrompt")}
          </p>
        </div>
        <div className="operations-hero-action">
          <span
            className={
              overview.program.status === "published" ? "healthy" : "attention"
            }
          >
            <i aria-hidden="true" />
            {copy(
              overview.program.status === "published"
                ? "merchant.dashboard.checkoutReady"
                : "merchant.dashboard.setupRequired",
            )}
          </span>
          {onScan && (
            <button className="primary-button" type="button" onClick={onScan}>
              {copy("merchant.till.open")}
            </button>
          )}
        </div>
      </section>
      <section
        className="stats home-stats"
        aria-label={copy("merchant.dashboard.glance")}
      >
        <OverviewStat
          label={copy("merchant.dashboard.todayTransactions")}
          value={overview.metrics.transactionsToday}
          note={copy("merchant.dashboard.localToday")}
          locale={locale}
        />
        <OverviewStat
          label={copy("merchant.dashboard.activeMembers")}
          value={overview.metrics.activeMembers}
          note={copy("merchant.dashboard.serverTruth")}
          locale={locale}
        />
        <OverviewStat
          label={copy("merchant.dashboard.rewardsRedeemed")}
          value={overview.metrics.rewardsRedeemedToday}
          note={copy("merchant.dashboard.localToday")}
          locale={locale}
        />
      </section>
      <section className="activity-stream">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{copy("merchant.overview.recent")}</p>
            <h2>{copy("merchant.dashboard.recent")}</h2>
          </div>
        </div>
        {overview.recent.items.length === 0 ? (
          <div className="empty-state compact-empty">
            <span className="empty-mark" aria-hidden="true" />
            <h2>{copy("merchant.dashboard.emptyTitle")}</h2>
            <p>{copy("merchant.dashboard.emptyBody")}</p>
          </div>
        ) : (
          <div className="transaction-list">
            {overview.recent.items.map((item) => {
              const effect = activityEffectValue(item);
              return (
                <div className="transaction-row" key={item.id}>
                  <div className="member-mark" aria-hidden="true">
                    {item.customer.displayMemberCode.slice(-2)}
                  </div>
                  <div>
                    <strong>{item.customer.displayMemberCode}</strong>
                    <small>
                      {copy(`merchant.activity.action.${item.action}`)}
                    </small>
                  </div>
                  <b>
                    {effect === null
                      ? copy("merchant.activity.rewardEffect")
                      : `${effect > 0 ? "+" : ""}${new Intl.NumberFormat(
                          locale,
                        ).format(effect)}`}
                  </b>
                  <time dateTime={item.occurredAt}>
                    {formatActivityTime(item, locale)}
                  </time>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function OverviewStat({
  label,
  value,
  note,
  locale,
}: {
  label: string;
  value: number;
  note: string;
  locale: Locale;
}) {
  return (
    <article>
      <span>{label}</span>
      <strong>{new Intl.NumberFormat(locale).format(value)}</strong>
      <small>{note}</small>
    </article>
  );
}
